import { OFFICIAL_SOURCES, LAST_VERIFIED } from '../officialSources';

/**
 * Authoritative permit & entry-fee data for Guided Journeys.
 * Fees verified against DNPWC, NTNC e-permit, and NTB — July 2026.
 * subjectToChange: true on all regulatory fees.
 */

export const PERMIT_OFFICES = {
	ntbKtm: {
		id: 'ntb-kathmandu',
		name: 'Nepal Tourism Board — Tourist Service Center',
		address: 'Bhrikutimandap, Kathmandu',
		hours: 'Sun–Fri 09:00–17:00 (closed Saturday & public holidays)',
		mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Nepal+Tourism+Board+Bhrikutimandap',
		lastVerified: LAST_VERIFIED,
	},
	ntncKtm: {
		id: 'ntnc-kathmandu',
		name: 'NTNC — ACAP / MCAP Permit Counter (Bhrikutimandap)',
		address: 'Bhrikutimandap, Kathmandu (near NTB)',
		hours: 'Sun–Fri 09:00–17:00',
		mapsUrl: 'https://www.google.com/maps/search/?api=1&query=NTNC+Bhrikutimandap',
		lastVerified: LAST_VERIFIED,
	},
	ntncPokhara: {
		id: 'ntnc-pokhara',
		name: 'NTNC — ACAP Permit Counter, Pokhara',
		address: 'Lakeside, Pokhara',
		hours: 'Sun–Fri 09:00–17:00',
		mapsUrl: 'https://www.google.com/maps/search/?api=1&query=NTNC+Pokhara+ACAP',
		lastVerified: LAST_VERIFIED,
	},
	dnpwcKtm: {
		id: 'dnpwc-kathmandu',
		name: 'DNPWC — National Park Entry Permits',
		address: 'Babar Mahal, Kathmandu',
		hours: 'Sun–Fri 09:00–17:00',
		mapsUrl: 'https://www.google.com/maps/search/?api=1&query=DNPWC+Kathmandu',
		lastVerified: LAST_VERIFIED,
	},
	chitwanGate: {
		id: 'chitwan-park',
		name: 'Chitwan National Park — Visitor Centre (Sauraha / Kasara)',
		address: 'Sauraha or Kasara, Chitwan District',
		hours: 'Daily during park season',
		mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Chitwan+National+Park+visitor+centre',
		lastVerified: LAST_VERIFIED,
	},
	bardiaGate: {
		id: 'bardia-park',
		name: 'Bardia National Park — Thakurdwara Gate',
		address: 'Thakurdwara, Bardiya District',
		hours: 'Daily during park season',
		mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Bardia+National+Park+Thakurdwara',
		lastVerified: LAST_VERIFIED,
	},
};

/** @typedef {{ name: string, feeForeignNpr?: number, feeSaarcNpr?: number, feeUsd?: number, feeNote?: string, where: string }} PermitLine */

/** @type {Record<string, { summary: string, timsRequired: boolean, lines: PermitLine[], documents: string[], offices: object[], budgetUsd: number, source: object, subjectToChange: boolean, lastVerified: string }>} */
export const PERMIT_DETAILS = {
	sagarmatha: {
		summary: 'Sagarmatha National Park entry + Khumbu Pasang Lhamu local permit',
		timsRequired: false,
		lines: [
			{ name: 'Sagarmatha National Park entry', feeForeignNpr: 3000, feeNote: '+ 13% VAT ≈ NPR 3,390', where: 'DNPWC counter Bhrikutimandap, Kathmandu — or Monjo gate on trail' },
			{ name: 'Khumbu Pasang Lhamu Rural Municipality permit', feeForeignNpr: 2000, where: 'Lukla on arrival (before Namche)' },
		],
		documents: ['Passport (original)', 'Passport photocopy', '2 passport-size photos', 'NPR cash only'],
		offices: [PERMIT_OFFICES.dnpwcKtm, PERMIT_OFFICES.ntbKtm],
		budgetUsd: 42,
		source: OFFICIAL_SOURCES.dnp,
		subjectToChange: true,
		lastVerified: LAST_VERIFIED,
	},
	acap: {
		summary: 'Annapurna Conservation Area Permit (ACAP)',
		timsRequired: false,
		lines: [
			{ name: 'ACAP entry permit', feeForeignNpr: 3000, feeSaarcNpr: 1000, feeNote: 'Tax included. Children under 10 free. Double fee if bought at trail checkpoint.', where: 'NTNC counter Bhrikutimandap Kathmandu, Lakeside Pokhara, or Besisahar — or online at epermit.ntnc.org.np' },
		],
		documents: ['Passport or valid ID', 'NPR cash or online payment'],
		offices: [PERMIT_OFFICES.ntncKtm, PERMIT_OFFICES.ntncPokhara],
		budgetUsd: 22,
		source: OFFICIAL_SOURCES.ntnc,
		subjectToChange: true,
		lastVerified: LAST_VERIFIED,
	},
	langtang: {
		summary: 'Langtang National Park entry permit',
		timsRequired: false,
		lines: [
			{ name: 'Langtang National Park entry', feeForeignNpr: 3000, feeSaarcNpr: 1500, feeNote: '+ 13% VAT', where: 'DNPWC counter Bhrikutimandap, Kathmandu — or Dhunche / Syabrubesi gate' },
		],
		documents: ['Passport (original)', 'Passport photocopy', 'NPR cash'],
		offices: [PERMIT_OFFICES.dnpwcKtm, PERMIT_OFFICES.ntbKtm],
		budgetUsd: 25,
		source: OFFICIAL_SOURCES.dnp,
		subjectToChange: true,
		lastVerified: LAST_VERIFIED,
	},
	manaslu: {
		summary: 'Manaslu Restricted Area Permit + Manaslu Conservation Area (MCAP)',
		timsRequired: false,
		lines: [
			{ name: 'Manaslu Restricted Area Permit', feeUsd: 100, feeNote: 'USD 100/week (Sep–Nov); USD 75/week (Dec–Aug). Minimum 7 days. Licensed guide mandatory.', where: 'Department of Immigration, Kathmandu — via registered agency' },
			{ name: 'MCAP (Manaslu Conservation Area)', feeForeignNpr: 2000, where: 'NTNC counter Bhrikutimandap or trail checkpoint' },
		],
		documents: ['Passport', 'Agency application', 'Guide license', 'USD cash for RAP'],
		offices: [PERMIT_OFFICES.ntncKtm, PERMIT_OFFICES.ntbKtm],
		budgetUsd: 130,
		source: OFFICIAL_SOURCES.ntb,
		subjectToChange: true,
		lastVerified: LAST_VERIFIED,
	},
	mustang: {
		summary: 'Upper Mustang Restricted Area Permit + ACAP',
		timsRequired: false,
		lines: [
			{ name: 'Upper Mustang Restricted Area Permit', feeUsd: 500, feeNote: 'USD 500 for first 10 days; USD 50/day thereafter. Licensed guide mandatory.', where: 'Department of Immigration, Kathmandu — via registered agency' },
			{ name: 'ACAP entry', feeForeignNpr: 3000, where: 'NTNC Kathmandu or Pokhara' },
		],
		documents: ['Passport', 'Agency application', 'Guide license', 'USD cash'],
		offices: [PERMIT_OFFICES.ntncKtm, PERMIT_OFFICES.ntbKtm],
		budgetUsd: 530,
		source: OFFICIAL_SOURCES.ntb,
		subjectToChange: true,
		lastVerified: LAST_VERIFIED,
	},
	dolpo: {
		summary: 'Upper Dolpo Restricted Area Permit',
		timsRequired: false,
		lines: [
			{ name: 'Upper Dolpo Restricted Area Permit', feeUsd: 500, feeNote: 'USD 500 for first 10 days; USD 50/day thereafter. Agency + guide mandatory.', where: 'Department of Immigration, Kathmandu — via registered agency' },
		],
		documents: ['Passport', 'Agency application', 'Guide license', 'USD cash'],
		offices: [PERMIT_OFFICES.ntbKtm],
		budgetUsd: 500,
		source: OFFICIAL_SOURCES.ntb,
		subjectToChange: true,
		lastVerified: LAST_VERIFIED,
	},
	kanchenjunga: {
		summary: 'Kanchenjunga Conservation Area + Restricted Area Permit',
		timsRequired: false,
		lines: [
			{ name: 'Kanchenjunga Restricted Area Permit', feeUsd: 20, feeNote: 'USD 20/week. Licensed guide mandatory.', where: 'Department of Immigration, Kathmandu — via registered agency' },
			{ name: 'Kanchenjunga Conservation Area entry', feeForeignNpr: 2000, where: 'Trail checkpoint Taplejung / Suketar' },
		],
		documents: ['Passport', 'Agency application', 'Guide license'],
		offices: [PERMIT_OFFICES.ntbKtm],
		budgetUsd: 40,
		source: OFFICIAL_SOURCES.ntb,
		subjectToChange: true,
		lastVerified: LAST_VERIFIED,
	},
	restricted: {
		summary: 'Restricted Area Permit (route-specific)',
		timsRequired: false,
		lines: [
			{ name: 'Restricted Area Permit', feeUsd: 100, feeNote: 'Fee varies by area — USD 20–500/week. Licensed guide mandatory. Apply via registered agency.', where: 'Department of Immigration, Kathmandu' },
		],
		documents: ['Passport', 'Agency application', 'Guide license'],
		offices: [PERMIT_OFFICES.ntbKtm],
		budgetUsd: 100,
		source: OFFICIAL_SOURCES.ntb,
		subjectToChange: true,
		lastVerified: LAST_VERIFIED,
	},
	chitwan: {
		summary: 'Chitwan National Park entry permit',
		timsRequired: false,
		lines: [
			{ name: 'Chitwan National Park entry', feeForeignNpr: 2000, feeSaarcNpr: 1000, feeNote: 'Per person. Often bundled by jungle lodge. VAT may apply.', where: 'Park visitor centre Sauraha / Kasara — or via your lodge' },
		],
		documents: ['Passport or ID'],
		offices: [PERMIT_OFFICES.chitwanGate],
		budgetUsd: 15,
		source: OFFICIAL_SOURCES.dnp,
		subjectToChange: true,
		lastVerified: LAST_VERIFIED,
	},
	bardia: {
		summary: 'Bardia National Park entry permit',
		timsRequired: false,
		lines: [
			{ name: 'Bardia National Park entry', feeForeignNpr: 1500, feeSaarcNpr: 750, feeNote: 'Per person. Often bundled by jungle lodge.', where: 'Thakurdwara park gate — or via your lodge' },
		],
		documents: ['Passport or ID'],
		offices: [PERMIT_OFFICES.bardiaGate],
		budgetUsd: 12,
		source: OFFICIAL_SOURCES.dnp,
		subjectToChange: true,
		lastVerified: LAST_VERIFIED,
	},
	park: {
		summary: 'National park / conservation area entry',
		timsRequired: false,
		lines: [
			{ name: 'National park entry', feeForeignNpr: 3000, feeNote: 'Fee varies by park — verify at DNPWC before travel', where: 'DNPWC counter Bhrikutimandap or park gate on trail' },
		],
		documents: ['Passport (original)', 'NPR cash'],
		offices: [PERMIT_OFFICES.dnpwcKtm],
		budgetUsd: 25,
		source: OFFICIAL_SOURCES.dnp,
		subjectToChange: true,
		lastVerified: LAST_VERIFIED,
	},
	general: {
		summary: 'Conservation area or local permit — verify before travel',
		timsRequired: false,
		lines: [
			{ name: 'Route-specific permit', feeForeignNpr: 2000, feeNote: 'Verify exact permit and fee with NTB or your agency for this specific route', where: 'NTB Bhrikutimandap or regional checkpoint' },
		],
		documents: ['Passport', 'NPR cash'],
		offices: [PERMIT_OFFICES.ntbKtm],
		budgetUsd: 20,
		source: OFFICIAL_SOURCES.ntb,
		subjectToChange: true,
		lastVerified: LAST_VERIFIED,
	},
	none: {
		summary: 'No trekking permit required',
		timsRequired: false,
		lines: [],
		documents: [],
		offices: [],
		budgetUsd: 0,
		source: OFFICIAL_SOURCES.ntb,
		subjectToChange: true,
		lastVerified: LAST_VERIFIED,
	},
};

/** UNESCO / heritage site entrance fees — Department of Archaeology / municipal rates */
export const HERITAGE_SITE_FEES = {
	'kathmandu-heritage': {
		summary: 'No trekking permit — pay entrance fees at each site gate',
		totalBudgetNpr: 4500,
		sites: [
			{ name: 'Kathmandu Durbar Square', feeForeignNpr: 1000 },
			{ name: 'Patan Durbar Square (Lalitpur)', feeForeignNpr: 1000 },
			{ name: 'Bhaktapur Durbar Square', feeForeignNpr: 1800, feeSaarcNpr: 500 },
			{ name: 'Swayambhunath (Monkey Temple)', feeForeignNpr: 200 },
			{ name: 'Pashupatinath Temple', feeForeignNpr: 1000 },
			{ name: 'Boudhanath Stupa', feeForeignNpr: 400 },
		],
		notes: 'Each site sells its own ticket at the gate. Patan, Bhaktapur, and Kathmandu municipal tickets are not interchangeable. Carry NPR cash in small notes.',
		source: OFFICIAL_SOURCES.archaeology,
		lastVerified: LAST_VERIFIED,
	},
	'lumbini-pilgrimage': {
		summary: 'No trekking permit — Lumbini Development Trust zone entry',
		totalBudgetNpr: 500,
		sites: [
			{ name: 'Lumbini Development Trust entry', feeForeignNpr: 500, feeNote: 'Covers monastic zone and Maya Devi Temple area' },
		],
		notes: 'Maya Devi Temple and monastic zone are in the Lumbini Development Trust area. Fly or bus from Kathmandu (8–10 hrs road).',
		source: OFFICIAL_SOURCES.ntb,
		lastVerified: LAST_VERIFIED,
	},
	'janakpur-pilgrimage': {
		summary: 'No trekking permit — temple donations and local transport only',
		totalBudgetNpr: 200,
		sites: [
			{ name: 'Janaki Mandir', feeForeignNpr: 0, feeNote: 'No official entry fee — donations welcome' },
			{ name: 'Mithila art galleries / local museums', feeForeignNpr: 200, feeNote: 'If visiting paid galleries' },
		],
		notes: 'Janakpur is in the Terai — fly or train from Kathmandu. Modest dress for temple visits.',
		source: OFFICIAL_SOURCES.ntb,
		lastVerified: LAST_VERIFIED,
	},
};

export function formatPermitLines(details) {
	if (!details || details.lines.length === 0) return details?.summary || 'No permit required.';
	return details.lines.map((l) => {
		const fee = l.feeForeignNpr
			? `NPR ${l.feeForeignNpr.toLocaleString()}`
			: l.feeUsd
				? `USD ${l.feeUsd}`
				: l.feeNote || 'See office';
		return `${l.name}: ${fee}${l.feeNote && l.feeForeignNpr ? ` (${l.feeNote})` : ''}. Obtain at: ${l.where}`;
	}).join(' ');
}

export function getPermitDetails(permitKey) {
	return PERMIT_DETAILS[permitKey] || PERMIT_DETAILS.general;
}

export function getHeritageFees(slug) {
	return HERITAGE_SITE_FEES[slug] || null;
}

export function totalPermitBudgetUsd(permitKey) {
	return getPermitDetails(permitKey).budgetUsd;
}
